import { Link, useParams } from 'react-router-dom';
import { usePageMeta } from '@/hooks/usePageMeta';
import { getNewsById, getRelatedNews, videos, formatDate, formatViews } from '@/utils/content';
import Breadcrumb from '@/components/feature/Breadcrumb';
import NewsCard from '@/components/feature/NewsCard';
import ShareButtons from '@/components/feature/ShareButtons';

const container = 'w-full max-w-[1280px] mx-auto px-4 md:px-8';

export default function NewsDetailPage() {
  const { id = '' } = useParams();
  const item = getNewsById(id);

  usePageMeta(
    item ? `${item.title} - ${item.categoryName} - 91.con` : '资讯详情 - 91.con',
    item ? item.summary : '91.con 娱乐资讯详情页',
    item ? `${item.tags.join(',')},${item.categoryName},91.con,娱乐资讯` : '娱乐资讯,91.con'
  );

  if (!item) {
    return (
      <section className={`${container} py-20`}>
        <div className="text-center max-w-lg mx-auto">
          <i className="ri-file-unknow-line text-5xl text-foreground-500" />
          <h1 className="font-heading text-2xl font-bold text-foreground-950 mt-4">未找到这篇资讯</h1>
          <p className="text-sm text-foreground-600 mt-2">内容可能已下线或链接有误，去首页看看最新热点吧。</p>
          <Link
            to="/"
            className="mt-6 inline-flex h-11 px-6 items-center rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            返回首页
          </Link>
        </div>
      </section>
    );
  }

  const related = getRelatedNews(item, 3);
  const hotVideos = [...videos].sort((a, b) => b.views - a.views).slice(0, 4);
  const relatedPool = getRelatedNews(item, 6);

  return (
    <>
      <section className="w-full bg-background-100 border-b border-background-200">
        <div className={`${container} py-6`}>
          <Breadcrumb items={[{ label: '资讯', to: '/category/film' }, { label: item.categoryName, to: `/category/${item.category}` }, { label: '正文' }]} />
        </div>
      </section>

      <section className={`${container} py-10 md:py-12`}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-10">
          <article className="lg:col-span-2">
            <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-primary-500/15 border border-primary-500/40 text-primary-300 text-xs font-medium">
              {item.categoryName}
            </span>
            <h1 className="font-heading text-2xl md:text-4xl font-extrabold text-foreground-950 mt-4 leading-snug">
              {item.title}
            </h1>
            <div className="flex items-center flex-wrap gap-4 mt-5 pb-5 border-b border-background-200 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5"><i className="ri-user-line" />{item.author}</span>
              <span className="flex items-center gap-1.5"><i className="ri-newspaper-line" />{item.source}</span>
              <span className="flex items-center gap-1.5"><i className="ri-calendar-line" />{formatDate(item.date)}</span>
              <span className="flex items-center gap-1.5"><i className="ri-eye-line" />{formatViews(item.views)} 阅读</span>
            </div>

            <div className="w-full h-56 md:h-96 rounded-lg overflow-hidden mt-6">
              <img
                src={item.cover}
                alt={item.title}
                title={`${item.title} - ${item.categoryName}`}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="mt-7 flex flex-col gap-5">
              {item.content.map((para, i) => (
                <p key={i} className="text-base text-foreground-700 leading-loose">{para}</p>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-2 mt-8 pt-6 border-t border-background-200">
              <span className="text-sm text-foreground-500 mr-1 flex items-center gap-1"><i className="ri-price-tag-3-line" />标签：</span>
              {item.tags.map((t) => (
                <Link
                  key={t}
                  to={`/search?q=${encodeURIComponent(t)}`}
                  className="px-3 py-1.5 rounded-full bg-secondary-100 border border-background-300 text-secondary-900 text-xs hover:border-accent-500/60 transition-colors cursor-pointer"
                >
                  #{t}
                </Link>
              ))}
            </div>

            <div className="mt-7">
              <ShareButtons title={item.title} />
            </div>
          </article>

          <aside className="flex flex-col gap-8">
            <div>
              <h2 className="font-heading text-lg font-bold text-foreground-950 flex items-center gap-2 mb-4">
                <span className="w-1 h-5 rounded-full bg-primary-500" />
                相关资讯
              </h2>
              <div className="flex flex-col gap-3">
                {related.map((n) => (
                  <Link
                    key={n.id}
                    to={`/news/${n.id}`}
                    className="group flex gap-3 p-3 rounded-lg bg-background-100 border border-background-200 hover:border-primary-500/60 transition-colors cursor-pointer"
                  >
                    <div className="w-24 h-16 rounded-md overflow-hidden shrink-0">
                      <img src={n.cover} alt={n.title} title={n.title} className="w-full h-full object-cover object-top" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-400 transition-colors line-clamp-2 leading-snug">
                        {n.title}
                      </h3>
                      <span className="text-xs text-foreground-500 mt-1 inline-block">{formatDate(n.date)}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            <div>
              <h2 className="font-heading text-lg font-bold text-foreground-950 flex items-center gap-2 mb-4">
                <span className="w-1 h-5 rounded-full bg-accent-500" />
                热门视频
              </h2>
              <div className="flex flex-col gap-2">
                {hotVideos.map((v, i) => (
                  <Link
                    key={v.id}
                    to={`/video/${v.id}`}
                    className="group flex items-center gap-3 p-2 rounded-md hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <span className="font-heading text-sm font-bold text-accent-500 w-5 text-center shrink-0">{i + 1}</span>
                    <span className="text-sm text-foreground-700 group-hover:text-accent-400 transition-colors line-clamp-2 leading-snug flex-1">
                      {v.title}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>

        <div className="mt-14">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 flex items-center gap-2.5 mb-6">
            <span className="w-1 h-6 rounded-full bg-primary-500" />
            猜你喜欢
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {relatedPool.map((n) => (
              <NewsCard key={n.id} item={n} />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}